import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { VocabularyTestTopic } from 'app/types';
import {
  DESKTOP_LAYOUT_BREAKPOINT,
  getVocabularyTestTopicKey,
} from 'pages/VocabularyTest/utils';

interface IProgressTopicsDropdownProps {
  testTopics: VocabularyTestTopic[];
  progress: number;
}

const getTopicStatusLabel = (status: VocabularyTestTopic['status']) => {
  if (status === 'PASSED') return 'Пройдено';
  if (status === 'CURRENT') return 'Поточна';
  return 'Попереду';
};

const getTopicStatusColor = (status: VocabularyTestTopic['status']) => {
  if (status === 'PASSED') return 'text.secondary';
  if (status === 'CURRENT') return 'primary.main';
  return 'text.primary';
};

const ProgressTopicsDropdown: React.FC<IProgressTopicsDropdownProps> = ({
  testTopics,
  progress,
}) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const currentTopicItemRef = useRef<HTMLLIElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Flip the popup upward when the trigger sits low enough on the page that
  // opening downward would push the (up to 420px tall) list past the
  // viewport bottom — most relevant for the StageNav copy of this dropdown
  // rendered after the category list, near the bottom of the page.
  const [openUpward, setOpenUpward] = useState(false);

  const menuOpen = Boolean(anchorEl);
  const firstCurrentTopic = testTopics.find(
    ({ status }) => status === 'CURRENT',
  );
  const firstCurrentTopicKey = firstCurrentTopic
    ? getVocabularyTestTopicKey(firstCurrentTopic)
    : null;

  useEffect(() => {
    if (!menuOpen) return;

    window.requestAnimationFrame(() => {
      const popup = popupRef.current;
      const currentTopicItem = currentTopicItemRef.current;
      if (!popup || !currentTopicItem) return;
      popup.scrollTop =
        currentTopicItem.offsetTop -
        popup.clientHeight / 2 +
        currentTopicItem.clientHeight / 2;
    });
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (
        triggerRef.current?.contains(target) ||
        popupRef.current?.contains(target)
      ) {
        return;
      }
      setAnchorEl(null);
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () =>
      document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [menuOpen]);

  const computeOpenUpward = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return false;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    return spaceBelow < 360 && spaceAbove > spaceBelow;
  };

  const toggleMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (!testTopics.length) return;
    event.preventDefault();
    if (menuOpen) {
      setAnchorEl(null);
      return;
    }
    setOpenUpward(computeOpenUpward());
    setAnchorEl(event.currentTarget);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (menuOpen) {
      setAnchorEl(null);
      return;
    }
    setOpenUpward(computeOpenUpward());
    setAnchorEl(triggerRef.current);
  };

  return (
    <Box
      sx={(theme) => ({
        flex: '1 1 100%',
        maxWidth: 'none',
        order: 0,
        [theme.breakpoints.up(DESKTOP_LAYOUT_BREAKPOINT)]: {
          flex: 1,
          maxWidth: 520,
          order: 1,
        },
      })}
    >
      <Box
        aria-expanded={menuOpen}
        aria-haspopup={'menu'}
        onKeyDown={handleKeyDown}
        onMouseDown={toggleMenu}
        ref={triggerRef}
        role={'button'}
        tabIndex={testTopics.length ? 0 : -1}
        sx={(theme) => ({
          alignItems: 'center',
          backgroundColor: theme.palette.background.paper,
          border: '1px solid',
          borderColor: menuOpen
            ? theme.palette.primary.main
            : theme.palette.primary.tableBorder,
          borderRadius: '8px',
          boxShadow: menuOpen
            ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`
            : 'none',
          boxSizing: 'border-box',
          color: theme.palette.text.primary,
          cursor: testTopics.length ? 'pointer' : 'default',
          display: 'flex',
          height: 42,
          justifyContent: 'space-between',
          minWidth: 260,
          px: 2,
          transition: theme.transitions.create(
            ['border-color', 'box-shadow', 'color'],
            { duration: theme.transitions.duration.shortest },
          ),
          width: '100%',
          '&:hover': {
            borderColor: testTopics.length
              ? theme.palette.primary.main
              : theme.palette.primary.tableBorder,
          },
          '& svg': {
            color: menuOpen
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
          },
        })}
      >
        <Typography
          sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3, userSelect: 'none' }}
          variant={'body1'}
        >
          {`Прогрес тестування: ${progress}%`}
        </Typography>
        <KeyboardArrowDownIcon
          fontSize={'small'}
          sx={{ transform: menuOpen ? 'rotate(180deg)' : 'none' }}
        />
      </Box>

      <Popper
        anchorEl={anchorEl}
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 16 } },
        ]}
        open={menuOpen}
        placement={openUpward ? 'top-start' : 'bottom-start'}
        // Above page content, but strictly BELOW the sticky status bar
        // (TestInProgress pins it at appBar + 1). When this list opens
        // upward from the bottom StageNav it must never cover the status
        // row — that bar always wins.
        sx={{ zIndex: (theme) => theme.zIndex.appBar }}
      >
        <Paper
          elevation={0}
          ref={popupRef}
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            border: '1px solid',
            borderColor: theme.palette.primary.tableBorder,
            borderRadius: '8px',
            boxShadow: `0 12px 32px ${alpha(
              theme.palette.common.black,
              theme.palette.mode === 'light' ? 0.14 : 0.34,
            )}`,
            color: theme.palette.text.primary,
            maxHeight: 420,
            overflowY: 'auto',
            width: { xs: 'calc(100vw - 32px)', sm: 520 },
          })}
        >
          <MenuList dense disablePadding>
            {testTopics.map(
              ({ categoryName, status, subCategoryName, wordSetType }) => (
                <MenuItem
                  disableRipple
                  key={getVocabularyTestTopicKey({ categoryName, subCategoryName, wordSetType })}
                  ref={
                    getVocabularyTestTopicKey({
                      categoryName,
                      subCategoryName,
                      wordSetType,
                    }) === firstCurrentTopicKey
                      ? currentTopicItemRef
                      : undefined
                  }
                  sx={{
                    alignItems: 'flex-start',
                    cursor: 'default',
                    gap: 1.5,
                    justifyContent: 'space-between',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: 'primary.main',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: getTopicStatusColor(status),
                      fontWeight: status === 'CURRENT' ? 700 : 400,
                    }}
                    variant={'body2'}
                  >
                    {`${categoryName}. ${subCategoryName}`}
                  </Typography>
                  <Typography
                    sx={{
                      color: getTopicStatusColor(status),
                      flexShrink: 0,
                      fontWeight: status === 'CURRENT' ? 700 : 400,
                    }}
                    variant={'caption'}
                  >
                    {getTopicStatusLabel(status)}
                  </Typography>
                </MenuItem>
              ),
            )}
          </MenuList>
        </Paper>
      </Popper>
    </Box>
  );
};

export default ProgressTopicsDropdown;
